import { useTranslation } from "react-i18next";
import { WifiOff, Wifi, Loader2 } from "lucide-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useSyncEngine } from "@/hooks/useSyncEngine";
import { cn } from "@/lib/utils";

export function OfflineBanner() {
  const { t } = useTranslation();
  const { isOnline, wasOffline } = useOnlineStatus();
  const { pendingCount, isSyncing, syncProgress, isOfflineCapable } = useSyncEngine();

  const showOffline = !isOnline;
  const showBackOnline = isOnline && wasOffline;
  const showSyncing = isOnline && isSyncing;
  const showPending = !isOnline && isOfflineCapable && pendingCount > 0;

  // Show nothing if online, never was offline, and no sync activity
  if (isOnline && !wasOffline && !isSyncing) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-300",
        showOffline
          ? "bg-destructive text-destructive-foreground animate-in slide-in-from-top"
          : "bg-primary text-primary-foreground animate-in slide-in-from-top",
      )}
    >
      {showOffline ? (
        <>
          <WifiOff className="h-4 w-4" />
          <span>{t("common.offline")}</span>
          {showPending && (
            <span className="ml-2 text-xs opacity-80">
              ({t("common.pendingChanges", { count: pendingCount })})
            </span>
          )}
        </>
      ) : showSyncing ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            {t("common.syncing")}
            {syncProgress && ` (${syncProgress.current}/${syncProgress.total})`}
          </span>
        </>
      ) : showBackOnline ? (
        <>
          <Wifi className="h-4 w-4" />
          <span>{t("common.backOnline")}</span>
        </>
      ) : null}
    </div>
  );
}
